import React, { useCallback, useContext, useEffect, useState } from 'react';

import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

import EmptyCard from '../../../../shared/components/v3/Cards/EmptyCard/EmptyCard';
import MetricsOnboardingCard from '../../../../shared/components/v3/Cards/MetricsOnboardingCard/MetricsOnboardingCard';
import RankingCard from '../../../../shared/components/v3/Cards/RankingCard/RankingCard';
import { BETTERFLIES_RANKING_PAGE_SIZE } from '../../../../shared/constants/metrics';
import { CompanyContext } from '../../../../shared/context/company/companyContext';
import { MetricsContext } from '../../../../shared/context/metrics/metricsContext';
import { mapToRankingDataItem } from '../../../../shared/handlers/dataMapper';
import { IRankingCardPage } from '../../../../shared/interfaces/cards';
import { ICircleData, ITimelineElement } from '../../../../shared/interfaces/charts';
import { IMetricsOnboardingByPeriod } from '../../../../shared/interfaces/metrics';
import MetricsByCompany from '../../../../shared/services/api/metrics/metricsByCompany/metrics';
import { TRANSLATIONS_GLOBAL } from '../../../../shared/translations/commons';
import { TRANSLATIONS_METRICS_NE } from '../../../../shared/translations/metrics';
import { formatToLocaleMonthDay } from '../../../../shared/utils/datetime/datetime';
import { toLocaleNumberFormat } from '../../../../shared/utils/utils';

const CollaboratorsMetrics = () => {
  const { company, locale } = useContext(CompanyContext);
  const { periodSelection } = useContext(MetricsContext);
  const [loadingOnboardingMetrics, setLoadingOnboardingMetrics] = useState<boolean>(false);
  const [onboardingMetricsError, setOnboardingMetricsError] = useState<boolean>(false);
  const [timelineChartData, setTimelineChartData] = useState<ITimelineElement[]>([]);
  const [stackedCircleChartData, setStackedCircleChartData] = useState<ICircleData[]>([]);
  const { t } = useTranslation();

  const getRankingPage = useCallback(
    async (limit: number, offset: number): Promise<IRankingCardPage> => {
      try {
        const response = await MetricsByCompany.getBetterfliesRankingMetrics(
          company.id,
          periodSelection.period,
          periodSelection.month,
          limit,
          offset
        );
        const leaderboard = response?.leaderboard ?? [];
        return {
          items: leaderboard.map(mapToRankingDataItem),
          hasMore: response?.pagination?.hasMore ?? leaderboard.length === limit
        };
      } catch (error: unknown) {
        if (isAxiosError(error) && error.response?.status === 404) {
          return { items: [], hasMore: false };
        }
        throw error;
      }
    },
    [company.id, periodSelection.period, periodSelection.month]
  );

  const handleOnboardingData = useCallback(
    (onboardingData: IMetricsOnboardingByPeriod) => {
      if (onboardingData) {
        setStackedCircleChartData([
          {
            value: onboardingData.amountMembersRegistered,
            label: t(TRANSLATIONS_METRICS_NE.onboardingCard.referenceInvited, {
              count: onboardingData.amountMembersRegistered
            }),
            tooltip: t(
              onboardingData.amountMembersRegistered > 1
                ? TRANSLATIONS_METRICS_NE.onboardingCard.referenceInvitedTooltipPlural
                : TRANSLATIONS_METRICS_NE.onboardingCard.referenceInvitedTooltip,
              {
                users: toLocaleNumberFormat(onboardingData.amountMembersRegistered)
              }
            )
          },
          {
            value: onboardingData.amountMembersActive,
            label: t(TRANSLATIONS_METRICS_NE.onboardingCard.referenceActive, {
              count: onboardingData.amountMembersActive
            }),
            tooltip: t(
              onboardingData.amountMembersActive > 1
                ? TRANSLATIONS_METRICS_NE.onboardingCard.referenceActiveTooltipPlural
                : TRANSLATIONS_METRICS_NE.onboardingCard.referenceActiveTooltip,
              {
                users: toLocaleNumberFormat(onboardingData.amountMembersActive)
              }
            )
          }
        ]);
        setTimelineChartData(
          onboardingData.historicalMetrics.map((item) => ({
            value: item.newOnboarded,
            timeLabel: `${formatToLocaleMonthDay(item.firstDay, locale)} - ${formatToLocaleMonthDay(
              item.lastDay,
              locale
            )}`
          }))
        );
      }
    },
    [locale, t]
  );

  const getOnboardingData = useCallback(async () => {
    setLoadingOnboardingMetrics(true);
    setOnboardingMetricsError(false);
    try {
      const response = await MetricsByCompany.getOnboardingMetricsByPeriod(
        company.id,
        periodSelection.period,
        periodSelection.month
      );
      handleOnboardingData(response);
    } catch {
      setOnboardingMetricsError(true);
    } finally {
      setLoadingOnboardingMetrics(false);
    }
  }, [company.id, periodSelection.period, periodSelection.month, handleOnboardingData]);

  useEffect(() => {
    void getOnboardingData();
  }, [company, getOnboardingData, periodSelection]);

  return (
    <div className="metrics-content-section-collaborators">
      <div className="first-component">
        <MetricsOnboardingCard
          title={t(TRANSLATIONS_GLOBAL.collaborators.title)}
          data={stackedCircleChartData}
          stackedCircleChartData={stackedCircleChartData}
          timelineChartData={timelineChartData}
          loading={loadingOnboardingMetrics}
          error={onboardingMetricsError}
          action={getOnboardingData}
        />
      </div>
      <div className="second-component">
        <RankingCard
          key={`${company.id}-${periodSelection.period}-${periodSelection.month}`}
          title={t(TRANSLATIONS_METRICS_NE.rankingCard.collaborators.title)}
          fetchPage={getRankingPage}
          pageSize={BETTERFLIES_RANKING_PAGE_SIZE}
          valueText={t(TRANSLATIONS_METRICS_NE.rankingCard.collaborators.valueText)}
          hasTabBar={false}
          emptyRankingCard={
            <EmptyCard
              primaryText={t(TRANSLATIONS_METRICS_NE.rankingCard.emptyState.emptyStateTitle)}
              secondaryText={t(TRANSLATIONS_METRICS_NE.rankingCard.emptyState.emptyStateDescription)}
            />
          }
        />
      </div>
    </div>
  );
};

export default CollaboratorsMetrics;
